/**
 * ShipBuilder.js
 *
 * June 2026 — Hangar / Shipyard.
 *
 * Procedurally builds 6 visually distinct spaceships onto a provided `shipRoot`
 * TransformNode. Every builder returns the same rig "hooks" the GameScene flight
 * code expects so any ship is a drop-in for the gameplay rig:
 *
 *   {
 *     thrusterMat,           // StandardMaterial whose emissive is pulsed by throttle
 *     ringMat,               // StandardMaterial whose emissive is pulsed by throttle
 *     navLights: {left,right}// nav-light materials (blinked by the day/night code)
 *     gearMeshes: [],        // landing gear meshes (currently kept static)
 *     cockpitOccluders: [],  // meshes hidden in cockpit/FPS view
 *     exhaustAnchors: [],    // Vector3 rear-nozzle positions for the exhaust FX
 *   }
 *
 * Pure Babylon, no React. Local frame: +Z forward, +X right, +Y up; the ship
 * rests with its deck around y≈0.4–1.5.
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
} from '@babylonjs/core';

function mat(scene, name, diffuse, opts = {}) {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = diffuse;
  m.specularColor = opts.specular || new Color3(0.5, 0.55, 0.6);
  if (opts.emissive) m.emissiveColor = opts.emissive;
  if (opts.specularPower != null) m.specularPower = opts.specularPower;
  m.maxSimultaneousLights = 8;
  return m;
}

function makeThrusterMats(scene) {
  const thrusterMat = new StandardMaterial('thrusterMat', scene);
  thrusterMat.diffuseColor = new Color3(0.18, 0.2, 0.25);
  thrusterMat.emissiveColor = new Color3(0.4, 0.55, 0.95);
  thrusterMat.specularColor = new Color3(0, 0, 0);

  const ringMat = new StandardMaterial('thrusterRingMat', scene);
  ringMat.diffuseColor = new Color3(0.05, 0.08, 0.14);
  ringMat.emissiveColor = new Color3(0.45, 0.65, 1.0);
  ringMat.specularColor = new Color3(0, 0, 0);
  return { thrusterMat, ringMat };
}

function makeNavMats(scene) {
  const navMatR = new StandardMaterial('navR', scene);
  navMatR.diffuseColor = new Color3(0.4, 0.1, 0.1);
  navMatR.emissiveColor = new Color3(1.0, 0.15, 0.15);
  navMatR.specularColor = new Color3(0, 0, 0);
  const navMatG = new StandardMaterial('navG', scene);
  navMatG.diffuseColor = new Color3(0.1, 0.4, 0.15);
  navMatG.emissiveColor = new Color3(0.15, 1.0, 0.25);
  navMatG.specularColor = new Color3(0, 0, 0);
  return { left: navMatR, right: navMatG };
}

function navOrb(scene, root, x, y, z, m, name) {
  const orb = MeshBuilder.CreateSphere(name, { diameter: 0.32, segments: 12 }, scene);
  orb.position.set(x, y, z);
  orb.material = m;
  orb.parent = root;
  return orb;
}

function addShadows(shadowGen, meshes) {
  if (!shadowGen) return;
  for (const m of meshes) { try { shadowGen.addShadowCaster(m); } catch (_e) { /* noop */ } }
}

// ===========================================================================
// 1) SCOUT — the original nimble single-seat surveyor.
// ===========================================================================
function buildScout(scene, shipRoot, { shadowGen }) {
  const { thrusterMat, ringMat } = makeThrusterMats(scene);
  const navLights = makeNavMats(scene);

  const hull = MeshBuilder.CreateSphere('hull', { diameterX: 3.4, diameterY: 1.6, diameterZ: 6.5, segments: 24 }, scene);
  hull.position.y = 1.5;
  hull.material = mat(scene, 'hullMat', new Color3(0.78, 0.82, 0.88), { specular: new Color3(0.9, 0.9, 1), specularPower: 96 });

  const cockpit = MeshBuilder.CreateSphere('cockpit', { diameter: 1.6, segments: 24 }, scene);
  cockpit.position.set(0, 2.1, 1.6);
  cockpit.scaling = new Vector3(1, 0.6, 1.1);
  cockpit.material = mat(scene, 'cockpitMat', new Color3(0.05, 0.12, 0.22), { emissive: new Color3(0.1, 0.3, 0.55), specular: new Color3(1, 1, 1) });

  const wingMat = mat(scene, 'wingMat', new Color3(0.5, 0.55, 0.62));
  const wingL = MeshBuilder.CreateBox('wingL', { width: 5, height: 0.18, depth: 1.6 }, scene);
  wingL.position.set(-2.4, 1.4, -0.5); wingL.rotation.z = -0.18; wingL.material = wingMat;
  const wingR = wingL.clone('wingR'); wingR.position.x = 2.4; wingR.rotation.z = 0.18;

  thrusterMat.diffuseColor = new Color3(0.18, 0.2, 0.25);
  [-1.2, 0, 1.2].forEach((x, i) => {
    const t = MeshBuilder.CreateCylinder(`thruster${i}`, { diameter: 0.55, height: 1.0 }, scene);
    t.rotation.x = Math.PI / 2; t.position.set(x, 1.4, -3.3); t.material = thrusterMat; t.parent = shipRoot;
  });

  const gearMat = mat(scene, 'gearMat', new Color3(0.3, 0.32, 0.35));
  const gearMeshes = [];
  [[-1.4, -2], [1.4, -2], [-1.4, 1.8], [1.4, 1.8]].forEach((p, i) => {
    const leg = MeshBuilder.CreateCylinder(`leg${i}`, { diameter: 0.18, height: 1.3 }, scene);
    leg.position.set(p[0], 0.65, p[1]); leg.material = gearMat; leg.parent = shipRoot; gearMeshes.push(leg);
  });

  [hull, cockpit, wingL, wingR].forEach((m) => { m.parent = shipRoot; });

  const stripe = MeshBuilder.CreateBox('shipStripe', { width: 0.4, height: 0.05, depth: 5.6 }, scene);
  stripe.position.set(0, 2.32, 0.4);
  stripe.material = mat(scene, 'shipStripeMat', new Color3(0.04, 0.05, 0.07), { emissive: new Color3(0.85, 0.45, 0.15), specular: new Color3(0, 0, 0) });
  stripe.parent = shipRoot;

  const belly = MeshBuilder.CreateBox('shipBelly', { width: 2.6, height: 0.05, depth: 4.2 }, scene);
  belly.position.set(0, 0.72, -0.1);
  belly.material = mat(scene, 'shipBellyMat', new Color3(0.06, 0.07, 0.08), { emissive: new Color3(0.12, 0.35, 0.55), specular: new Color3(0, 0, 0) });
  belly.parent = shipRoot;

  navOrb(scene, shipRoot, -4.7, 1.4, -0.6, navLights.left, 'navL');
  navOrb(scene, shipRoot, 4.7, 1.4, -0.6, navLights.right, 'navR');

  const antMat = mat(scene, 'antMat', new Color3(0.15, 0.16, 0.18), { specular: new Color3(0.4, 0.4, 0.4) });
  [-1.2, 1.2].forEach((zOff, i) => {
    const ant = MeshBuilder.CreateCylinder(`ant${i}`, { diameter: 0.06, height: 1.6 }, scene);
    ant.position.set(0, 2.7, zOff); ant.material = antMat; ant.parent = shipRoot;
    const tip = MeshBuilder.CreateSphere(`antTip${i}`, { diameter: 0.16, segments: 10 }, scene);
    tip.position.set(0, 3.4, zOff); tip.parent = shipRoot;
    tip.material = mat(scene, `antTipMat${i}`, new Color3(0.2, 0.05, 0.05), { emissive: new Color3(1.0, 0.25, 0.10), specular: new Color3(0, 0, 0) });
  });

  [-1.2, 0, 1.2].forEach((x, i) => {
    const ring = MeshBuilder.CreateTorus(`thrusterRing${i}`, { diameter: 0.78, thickness: 0.06, tessellation: 24 }, scene);
    ring.rotation.x = Math.PI / 2; ring.position.set(x, 1.4, -3.85); ring.material = ringMat; ring.parent = shipRoot;
  });

  addShadows(shadowGen, [hull, cockpit, wingL, wingR, belly, stripe]);

  return {
    thrusterMat, ringMat, navLights, gearMeshes,
    cockpitOccluders: [hull, cockpit, stripe],
    exhaustAnchors: [new Vector3(-1.2, 1.4, -3.85), new Vector3(0, 1.4, -3.85), new Vector3(1.2, 1.4, -3.85)],
  };
}

// ===========================================================================
// 2) INTERCEPTOR — sleek swept-wing strike fighter.
// ===========================================================================
function buildInterceptor(scene, shipRoot, { shadowGen }) {
  const { thrusterMat, ringMat } = makeThrusterMats(scene);
  thrusterMat.emissiveColor = new Color3(1.0, 0.5, 0.15);
  ringMat.emissiveColor = new Color3(1.0, 0.55, 0.2);
  const navLights = makeNavMats(scene);
  const bodyMat = mat(scene, 'icHull', new Color3(0.62, 0.12, 0.14), { specular: new Color3(0.9, 0.6, 0.6), specularPower: 120 });
  const trimMat = mat(scene, 'icTrim', new Color3(0.12, 0.13, 0.16), { emissive: new Color3(0.6, 0.2, 0.05) });

  // Long needle fuselage
  const fuse = MeshBuilder.CreateCylinder('icFuse', { diameterTop: 0.5, diameterBottom: 1.3, height: 7.0, tessellation: 16 }, scene);
  fuse.rotation.x = Math.PI / 2; fuse.position.set(0, 1.4, 0.4); fuse.material = bodyMat; fuse.parent = shipRoot;
  // Pointed nose cone
  const nose = MeshBuilder.CreateCylinder('icNose', { diameterTop: 0, diameterBottom: 0.5, height: 2.4, tessellation: 16 }, scene);
  nose.rotation.x = Math.PI / 2; nose.position.set(0, 1.4, 4.6); nose.material = bodyMat; nose.parent = shipRoot;
  // Bubble canopy
  const canopy = MeshBuilder.CreateSphere('icCanopy', { diameter: 1.1, segments: 18 }, scene);
  canopy.scaling = new Vector3(1, 0.55, 1.7); canopy.position.set(0, 1.95, 1.4);
  canopy.material = mat(scene, 'icCanopyMat', new Color3(0.04, 0.1, 0.18), { emissive: new Color3(0.1, 0.35, 0.6), specular: new Color3(1, 1, 1) });
  canopy.parent = shipRoot;

  // Swept-back delta wings
  const wingMat = mat(scene, 'icWing', new Color3(0.5, 0.1, 0.12));
  [-1, 1].forEach((s) => {
    const wing = MeshBuilder.CreateBox(`icWing${s}`, { width: 4.4, height: 0.14, depth: 2.6 }, scene);
    wing.position.set(s * 2.6, 1.25, -1.2); wing.rotation.y = s * 0.5; wing.rotation.z = -s * 0.12;
    wing.material = wingMat; wing.parent = shipRoot;
    // wingtip missile rail
    const rail = MeshBuilder.CreateCylinder(`icRail${s}`, { diameter: 0.22, height: 2.0 }, scene);
    rail.rotation.x = Math.PI / 2; rail.position.set(s * 4.4, 1.25, -1.0); rail.material = trimMat; rail.parent = shipRoot;
    navOrb(scene, shipRoot, s * 4.4, 1.25, 0.1, s < 0 ? navLights.left : navLights.right, `icNav${s}`);
  });
  // Twin tail fins
  [-1, 1].forEach((s) => {
    const fin = MeshBuilder.CreateBox(`icFin${s}`, { width: 0.12, height: 1.7, depth: 1.6 }, scene);
    fin.position.set(s * 0.7, 2.1, -2.8); fin.rotation.x = 0.3; fin.material = wingMat; fin.parent = shipRoot;
  });

  // Twin rear thrusters + rings
  const anchors = [];
  [-0.7, 0.7].forEach((x, i) => {
    const t = MeshBuilder.CreateCylinder(`icThr${i}`, { diameter: 0.85, height: 1.2 }, scene);
    t.rotation.x = Math.PI / 2; t.position.set(x, 1.4, -3.4); t.material = thrusterMat; t.parent = shipRoot;
    const ring = MeshBuilder.CreateTorus(`icRing${i}`, { diameter: 1.0, thickness: 0.08, tessellation: 24 }, scene);
    ring.rotation.x = Math.PI / 2; ring.position.set(x, 1.4, -4.0); ring.material = ringMat; ring.parent = shipRoot;
    anchors.push(new Vector3(x, 1.4, -4.0));
  });

  addShadows(shadowGen, [fuse, nose, canopy]);
  return { thrusterMat, ringMat, navLights, gearMeshes: [], cockpitOccluders: [fuse, nose, canopy], exhaustAnchors: anchors };
}

// ===========================================================================
// 3) HAULER — boxy industrial cargo freighter with container pods.
// ===========================================================================
function buildHauler(scene, shipRoot, { shadowGen }) {
  const { thrusterMat, ringMat } = makeThrusterMats(scene);
  const navLights = makeNavMats(scene);
  const hullMat = mat(scene, 'hlHull', new Color3(0.55, 0.5, 0.32), { specular: new Color3(0.3, 0.3, 0.28) });
  const trimMat = mat(scene, 'hlTrim', new Color3(0.2, 0.21, 0.22), { emissive: new Color3(0.3, 0.2, 0.04) });

  // Big rectangular main hull
  const body = MeshBuilder.CreateBox('hlBody', { width: 4.2, height: 2.6, depth: 8.0 }, scene);
  body.position.set(0, 1.8, -0.5); body.material = hullMat; body.parent = shipRoot;
  // Cockpit box at front
  const cab = MeshBuilder.CreateBox('hlCab', { width: 3.0, height: 1.7, depth: 2.0 }, scene);
  cab.position.set(0, 2.2, 4.0); cab.material = hullMat; cab.parent = shipRoot;
  const glass = MeshBuilder.CreateBox('hlGlass', { width: 2.6, height: 0.9, depth: 0.2 }, scene);
  glass.position.set(0, 2.4, 5.0);
  glass.material = mat(scene, 'hlGlass', new Color3(0.05, 0.12, 0.2), { emissive: new Color3(0.12, 0.32, 0.5) });
  glass.parent = shipRoot;

  // Container pods bolted on each side (varied colours)
  const podColors = [new Color3(0.7, 0.35, 0.2), new Color3(0.25, 0.45, 0.6), new Color3(0.4, 0.55, 0.3), new Color3(0.6, 0.5, 0.25)];
  let ci = 0;
  [-1, 1].forEach((s) => {
    [-1.6, 1.6].forEach((zo) => {
      const pod = MeshBuilder.CreateBox(`hlPod${s}_${zo}`, { width: 1.5, height: 1.8, depth: 3.0 }, scene);
      pod.position.set(s * 2.9, 1.6, zo - 0.5);
      pod.material = mat(scene, `hlPodMat${ci}`, podColors[ci % podColors.length].clone());
      pod.parent = shipRoot; ci++;
    });
    navOrb(scene, shipRoot, s * 2.9, 2.6, 4.0, s < 0 ? navLights.left : navLights.right, `hlNav${s}`);
  });

  // Spine pipes
  [-0.9, 0.9].forEach((x, i) => {
    const pipe = MeshBuilder.CreateCylinder(`hlPipe${i}`, { diameter: 0.3, height: 7.4 }, scene);
    pipe.rotation.x = Math.PI / 2; pipe.position.set(x, 3.2, -0.5); pipe.material = trimMat; pipe.parent = shipRoot;
  });

  // Landing struts
  const gearMat = mat(scene, 'hlGear', new Color3(0.25, 0.26, 0.28));
  const gearMeshes = [];
  [[-1.8, -3], [1.8, -3], [-1.8, 2.5], [1.8, 2.5]].forEach((p, i) => {
    const leg = MeshBuilder.CreateCylinder(`hlLeg${i}`, { diameter: 0.3, height: 1.4 }, scene);
    leg.position.set(p[0], 0.7, p[1]); leg.material = gearMat; leg.parent = shipRoot; gearMeshes.push(leg);
  });

  // Four big rear thrusters + rings
  const anchors = [];
  [[-1.3, 1.2], [1.3, 1.2], [-1.3, 2.6], [1.3, 2.6]].forEach((p, i) => {
    const t = MeshBuilder.CreateCylinder(`hlThr${i}`, { diameter: 1.0, height: 1.3 }, scene);
    t.rotation.x = Math.PI / 2; t.position.set(p[0], p[1], -4.4); t.material = thrusterMat; t.parent = shipRoot;
    const ring = MeshBuilder.CreateTorus(`hlRing${i}`, { diameter: 1.2, thickness: 0.1, tessellation: 24 }, scene);
    ring.rotation.x = Math.PI / 2; ring.position.set(p[0], p[1], -5.0); ring.material = ringMat; ring.parent = shipRoot;
    anchors.push(new Vector3(p[0], p[1], -5.0));
  });

  addShadows(shadowGen, [body, cab]);
  return { thrusterMat, ringMat, navLights, gearMeshes, cockpitOccluders: [body, cab, glass], exhaustAnchors: anchors };
}

// ===========================================================================
// 4) VOYAGER — saucer-hulled deep-space explorer with a sensor dish.
// ===========================================================================
function buildVoyager(scene, shipRoot, { shadowGen }) {
  const { thrusterMat, ringMat } = makeThrusterMats(scene);
  thrusterMat.emissiveColor = new Color3(0.3, 0.85, 0.8);
  ringMat.emissiveColor = new Color3(0.35, 0.9, 0.85);
  const navLights = makeNavMats(scene);
  const hullMat = mat(scene, 'vyHull', new Color3(0.82, 0.84, 0.9), { specular: new Color3(0.8, 0.85, 0.95), specularPower: 80 });
  const trimMat = mat(scene, 'vyTrim', new Color3(0.2, 0.4, 0.45), { emissive: new Color3(0.1, 0.5, 0.55) });

  // Flattened saucer disc
  const saucer = MeshBuilder.CreateCylinder('vySaucer', { diameter: 8.0, height: 1.1, tessellation: 40 }, scene);
  saucer.position.set(0, 1.5, 0); saucer.material = hullMat; saucer.parent = shipRoot;
  // Lower hull lens
  const lens = MeshBuilder.CreateSphere('vyLens', { diameter: 4.0, segments: 24 }, scene);
  lens.scaling = new Vector3(1, 0.4, 1); lens.position.set(0, 1.0, 0); lens.material = trimMat; lens.parent = shipRoot;
  // Central command dome
  const dome = MeshBuilder.CreateSphere('vyDome', { diameter: 2.6, segments: 24 }, scene);
  dome.scaling = new Vector3(1, 0.6, 1); dome.position.set(0, 2.1, 0.3);
  dome.material = mat(scene, 'vyDomeMat', new Color3(0.06, 0.14, 0.2), { emissive: new Color3(0.1, 0.4, 0.55), specular: new Color3(1, 1, 1) });
  dome.parent = shipRoot;
  // Glowing rim ring
  const rim = MeshBuilder.CreateTorus('vyRim', { diameter: 8.0, thickness: 0.16, tessellation: 48 }, scene);
  rim.position.set(0, 1.5, 0);
  rim.material = mat(scene, 'vyRimMat', new Color3(0.1, 0.5, 0.55), { emissive: new Color3(0.2, 0.85, 0.9), specular: new Color3(0, 0, 0) });
  rim.parent = shipRoot;

  // Steerable sensor dish on a mast
  const mast = MeshBuilder.CreateCylinder('vyMast', { diameter: 0.18, height: 1.4 }, scene);
  mast.position.set(0, 2.9, -0.5); mast.material = trimMat; mast.parent = shipRoot;
  const dish = MeshBuilder.CreateCylinder('vyDish', { diameterTop: 1.8, diameterBottom: 0.2, height: 0.7, tessellation: 28 }, scene);
  dish.position.set(0, 3.6, -0.5); dish.rotation.x = -0.5;
  dish.material = mat(scene, 'vyDishMat', new Color3(0.7, 0.72, 0.78), { emissive: new Color3(0.05, 0.2, 0.22) });
  dish.parent = shipRoot;

  navOrb(scene, shipRoot, -4.0, 1.5, 0, navLights.left, 'vyNavL');
  navOrb(scene, shipRoot, 4.0, 1.5, 0, navLights.right, 'vyNavR');

  // Rear engine pods (twin) + rings
  const anchors = [];
  [-2.2, 2.2].forEach((x, i) => {
    const pod = MeshBuilder.CreateCylinder(`vyPod${i}`, { diameter: 0.9, height: 2.6 }, scene);
    pod.rotation.x = Math.PI / 2; pod.position.set(x, 1.5, -3.4); pod.material = hullMat; pod.parent = shipRoot;
    const ring = MeshBuilder.CreateTorus(`vyRing${i}`, { diameter: 1.0, thickness: 0.08, tessellation: 24 }, scene);
    ring.rotation.x = Math.PI / 2; ring.position.set(x, 1.5, -4.6); ring.material = ringMat; ring.parent = shipRoot;
    anchors.push(new Vector3(x, 1.5, -4.7));
  });

  addShadows(shadowGen, [saucer, dome]);
  return { thrusterMat, ringMat, navLights, gearMeshes: [], cockpitOccluders: [saucer, dome, lens], exhaustAnchors: anchors };
}

// ===========================================================================
// 5) GUNSHIP — armoured heavy warship bristling with cannons.
// ===========================================================================
function buildGunship(scene, shipRoot, { shadowGen }) {
  const { thrusterMat, ringMat } = makeThrusterMats(scene);
  thrusterMat.emissiveColor = new Color3(1.0, 0.3, 0.2);
  ringMat.emissiveColor = new Color3(1.0, 0.35, 0.2);
  const navLights = makeNavMats(scene);
  const armorMat = mat(scene, 'gsArmor', new Color3(0.24, 0.27, 0.3), { specular: new Color3(0.3, 0.32, 0.36) });
  const trimMat = mat(scene, 'gsTrim', new Color3(0.3, 0.07, 0.05), { emissive: new Color3(0.7, 0.12, 0.05) });

  // Thick angular hull (two stacked blocks)
  const lower = MeshBuilder.CreateBox('gsLower', { width: 4.6, height: 1.8, depth: 8.2 }, scene);
  lower.position.set(0, 1.4, -0.4); lower.material = armorMat; lower.parent = shipRoot;
  const upper = MeshBuilder.CreateBox('gsUpper', { width: 3.0, height: 1.4, depth: 5.0 }, scene);
  upper.position.set(0, 2.7, -0.6); upper.material = armorMat; upper.parent = shipRoot;
  // Angled armoured nose
  const nose = MeshBuilder.CreateCylinder('gsNose', { diameterTop: 0.6, diameterBottom: 2.2, height: 2.6, tessellation: 6 }, scene);
  nose.rotation.x = Math.PI / 2; nose.position.set(0, 1.5, 4.8); nose.material = armorMat; nose.parent = shipRoot;
  // Bridge slit
  const slit = MeshBuilder.CreateBox('gsSlit', { width: 2.0, height: 0.4, depth: 0.2 }, scene);
  slit.position.set(0, 3.0, 1.9);
  slit.material = mat(scene, 'gsSlitMat', new Color3(0.1, 0.02, 0.02), { emissive: new Color3(0.8, 0.15, 0.1) });
  slit.parent = shipRoot;

  // Forward twin cannons
  [-1.1, 1.1].forEach((x, i) => {
    const barrel = MeshBuilder.CreateCylinder(`gsCannon${i}`, { diameter: 0.4, height: 4.5 }, scene);
    barrel.rotation.x = Math.PI / 2; barrel.position.set(x, 1.2, 4.0); barrel.material = trimMat; barrel.parent = shipRoot;
    const muzzle = MeshBuilder.CreateCylinder(`gsMuzzle${i}`, { diameter: 0.55, height: 0.5 }, scene);
    muzzle.rotation.x = Math.PI / 2; muzzle.position.set(x, 1.2, 6.1);
    muzzle.material = mat(scene, `gsMuzMat${i}`, new Color3(0.1, 0.1, 0.1), { emissive: new Color3(0.6, 0.2, 0.05) });
    muzzle.parent = shipRoot;
  });

  // Side weapon pods + armoured fins
  [-1, 1].forEach((s) => {
    const pod = MeshBuilder.CreateBox(`gsPod${s}`, { width: 1.0, height: 1.0, depth: 3.2 }, scene);
    pod.position.set(s * 2.9, 1.7, -0.5); pod.material = armorMat; pod.parent = shipRoot;
    const fin = MeshBuilder.CreateBox(`gsFin${s}`, { width: 0.16, height: 2.2, depth: 2.4 }, scene);
    fin.position.set(s * 2.4, 2.6, -2.6); fin.rotation.z = s * 0.3; fin.material = armorMat; fin.parent = shipRoot;
    navOrb(scene, shipRoot, s * 3.4, 1.7, 0.6, s < 0 ? navLights.left : navLights.right, `gsNav${s}`);
  });

  const gearMat = mat(scene, 'gsGear', new Color3(0.2, 0.21, 0.23));
  const gearMeshes = [];
  [[-1.7, -3.2], [1.7, -3.2], [-1.7, 3], [1.7, 3]].forEach((p, i) => {
    const leg = MeshBuilder.CreateCylinder(`gsLeg${i}`, { diameter: 0.34, height: 1.3 }, scene);
    leg.position.set(p[0], 0.55, p[1]); leg.material = gearMat; leg.parent = shipRoot; gearMeshes.push(leg);
  });

  // Four heavy rear thrusters + rings
  const anchors = [];
  [[-1.4, 1.0], [1.4, 1.0], [-1.4, 2.4], [1.4, 2.4]].forEach((p, i) => {
    const t = MeshBuilder.CreateCylinder(`gsThr${i}`, { diameter: 0.95, height: 1.2 }, scene);
    t.rotation.x = Math.PI / 2; t.position.set(p[0], p[1], -4.6); t.material = thrusterMat; t.parent = shipRoot;
    const ring = MeshBuilder.CreateTorus(`gsRing${i}`, { diameter: 1.15, thickness: 0.1, tessellation: 24 }, scene);
    ring.rotation.x = Math.PI / 2; ring.position.set(p[0], p[1], -5.2); ring.material = ringMat; ring.parent = shipRoot;
    anchors.push(new Vector3(p[0], p[1], -5.2));
  });

  addShadows(shadowGen, [lower, upper, nose]);
  return { thrusterMat, ringMat, navLights, gearMeshes, cockpitOccluders: [lower, upper, nose, slit], exhaustAnchors: anchors };
}

// ===========================================================================
// 6) MONARCH — elegant flagship cruiser with sweeping wings + bridge tower.
// ===========================================================================
function buildMonarch(scene, shipRoot, { shadowGen }) {
  const { thrusterMat, ringMat } = makeThrusterMats(scene);
  thrusterMat.emissiveColor = new Color3(0.85, 0.7, 0.3);
  ringMat.emissiveColor = new Color3(1.0, 0.82, 0.4);
  const navLights = makeNavMats(scene);
  const hullMat = mat(scene, 'mnHull', new Color3(0.9, 0.91, 0.94), { specular: new Color3(0.9, 0.88, 0.8), specularPower: 110 });
  const goldMat = mat(scene, 'mnGold', new Color3(0.8, 0.66, 0.3), { emissive: new Color3(0.45, 0.34, 0.08), specular: new Color3(1, 0.9, 0.6) });

  // Long elegant spindle hull
  const hull = MeshBuilder.CreateSphere('mnHull', { diameterX: 3.6, diameterY: 2.4, diameterZ: 11.0, segments: 28 }, scene);
  hull.position.set(0, 1.9, 0); hull.material = hullMat; hull.parent = shipRoot;
  // Tapered prow
  const prow = MeshBuilder.CreateCylinder('mnProw', { diameterTop: 0, diameterBottom: 1.6, height: 3.0, tessellation: 20 }, scene);
  prow.rotation.x = Math.PI / 2; prow.position.set(0, 1.9, 6.4); prow.material = hullMat; prow.parent = shipRoot;
  // Bridge tower with viewport
  const tower = MeshBuilder.CreateBox('mnTower', { width: 1.8, height: 1.8, depth: 2.6 }, scene);
  tower.position.set(0, 3.4, 1.4); tower.material = hullMat; tower.parent = shipRoot;
  const view = MeshBuilder.CreateBox('mnView', { width: 1.5, height: 0.7, depth: 0.2 }, scene);
  view.position.set(0, 3.6, 2.7);
  view.material = mat(scene, 'mnViewMat', new Color3(0.05, 0.12, 0.2), { emissive: new Color3(0.15, 0.4, 0.6) });
  view.parent = shipRoot;

  // Sweeping wings with gold trim
  [-1, 1].forEach((s) => {
    const wing = MeshBuilder.CreateBox(`mnWing${s}`, { width: 5.5, height: 0.2, depth: 3.4 }, scene);
    wing.position.set(s * 3.4, 1.7, -1.4); wing.rotation.y = s * 0.32; wing.rotation.z = -s * 0.16;
    wing.material = hullMat; wing.parent = shipRoot;
    const edge = MeshBuilder.CreateBox(`mnEdge${s}`, { width: 5.5, height: 0.08, depth: 0.4 }, scene);
    edge.position.set(s * 3.4, 1.8, -2.9); edge.rotation.y = s * 0.32; edge.material = goldMat; edge.parent = shipRoot;
    navOrb(scene, shipRoot, s * 6.0, 1.75, -1.4, s < 0 ? navLights.left : navLights.right, `mnNav${s}`);
  });
  // Gold spine stripe
  const stripe = MeshBuilder.CreateBox('mnStripe', { width: 0.5, height: 0.08, depth: 9.0 }, scene);
  stripe.position.set(0, 3.1, -0.4); stripe.material = goldMat; stripe.parent = shipRoot;

  const gearMat = mat(scene, 'mnGear', new Color3(0.5, 0.5, 0.52));
  const gearMeshes = [];
  [[-1.6, -3.4], [1.6, -3.4], [-1.6, 3.4], [1.6, 3.4]].forEach((p, i) => {
    const leg = MeshBuilder.CreateCylinder(`mnLeg${i}`, { diameter: 0.26, height: 1.5 }, scene);
    leg.position.set(p[0], 0.6, p[1]); leg.material = gearMat; leg.parent = shipRoot; gearMeshes.push(leg);
  });

  // Twin ring engines at the rear
  const anchors = [];
  [-1.7, 1.7].forEach((x, i) => {
    const housing = MeshBuilder.CreateCylinder(`mnHousing${i}`, { diameter: 1.6, height: 1.6, tessellation: 28 }, scene);
    housing.rotation.x = Math.PI / 2; housing.position.set(x, 1.9, -4.6); housing.material = hullMat; housing.parent = shipRoot;
    const ring = MeshBuilder.CreateTorus(`mnRing${i}`, { diameter: 1.5, thickness: 0.14, tessellation: 28 }, scene);
    ring.rotation.x = Math.PI / 2; ring.position.set(x, 1.9, -5.3); ring.material = ringMat; ring.parent = shipRoot;
    const core = MeshBuilder.CreateCylinder(`mnCore${i}`, { diameter: 0.9, height: 0.5 }, scene);
    core.rotation.x = Math.PI / 2; core.position.set(x, 1.9, -5.2); core.material = thrusterMat; core.parent = shipRoot;
    anchors.push(new Vector3(x, 1.9, -5.4));
  });

  addShadows(shadowGen, [hull, prow, tower]);
  return { thrusterMat, ringMat, navLights, gearMeshes, cockpitOccluders: [hull, prow, tower, view, stripe], exhaustAnchors: anchors };
}

const BUILDERS = {
  scout: buildScout,
  interceptor: buildInterceptor,
  hauler: buildHauler,
  voyager: buildVoyager,
  gunship: buildGunship,
  monarch: buildMonarch,
};

/**
 * Build the requested ship onto `shipRoot`. Falls back to the Scout if the key
 * is unknown. Returns the gameplay rig hooks (see file header).
 */
export function buildShip(scene, shipRoot, visual, opts = {}) {
  const fn = BUILDERS[visual] || BUILDERS.scout;
  return fn(scene, shipRoot, opts);
}

export const SHIP_VISUALS = Object.keys(BUILDERS);
